<script setup lang="ts">
import { useIntersectionObserver } from '@vueuse/core'
import { ProductTileProps } from '~/types/Product'

const props = defineProps<ProductTileProps>()
const i18n = useI18n()
const route = useRoute()
const { trackImpression } = useProductImpressions(i18n.locale.value)
const { trackBrowseResultClick, trackRecommendationClick, trackSearchResultClick } = useConstructorTracker()
const { attributeSearchClick } = useSearchAttribution()
const location = route.path
const category = location?.includes('/search') ? 'Search Page' : 'Product Listing Page'
const currencyCode = localeConfig[i18n.locale.value]?.currency
const productTile = ref<HTMLElement | null>(null)
const constructorItemParam = ref<RequiredItemParam>({
  itemId: props.constructorItemId || props.id,
  itemName: props.name || '',
  variationId: props.id,
})
const filteredPromotions = computed(() => {
  if (!props.promotions) return null
  return props.promotions?.filter((promo) => {
    return props.isAuthenticated ? promo.customerAvailability !== 'guest' : promo.customerAvailability !== 'member'
  })
})
const badgeContent = computed(() => {
  const badges = [...props.badgeText]
  if (props.isClearance) badges.push(i18n.t('product_tile.text.clearance'))
  return badges.slice(0, 2)
})

const unshownSwatchCount = computed(() => {
  if (!props.swatches) return 0
  return props.swatches.length > 4 ? props.swatches.length - 4 : 0
})

const activeSwatch = ref<MappedConstructorSwatch | undefined>()

const getSwatchSpriteName = (swatch: MappedConstructorSwatch) => {
  const { colour } = swatch
  const attrName = Object.keys(colour)[0]
  const id = attrName ? colour[attrName] : undefined
  if (!attrName || !id) return undefined
  return getSpriteName({ attrName, id })
}

// No `sizes=auto`: browsers only honour it as the first entry, where it overrides the phone density cap.
const tileImageSizes = computed(() => props.imageSizes ?? productImageSizes.tile)

const selectedSwatchData = computed(() => {
  return {
    id: activeSwatch.value?.id || props.id,
    name: activeSwatch.value?.name || props.name,
    url: activeSwatch.value?.url || props.pdpUrl,
    defaultImage: activeSwatch.value?.defaultImage || props.defaultImage,
    lifestyleImage: activeSwatch.value?.lifestyleImage || props.lifestyleImage,
    allImages: activeSwatch.value?.allImages || props.allImages,
  }
})

const isAlreadyInBag = computed(() => {
  return useBasketsStore().isItemInBasket(selectedSwatchData.value.id)
})

const starsFile = computed(() => {
  if (props.rating > 0) {
    return `rating-${props.rating.toFixed(1).replace('.', '_')}.svg`
  }
  return 'rating-0_0.svg'
})

const trackImpressions = () => {
  const payload = {
    group: props.listName,
    category,
    label: undefined,
    info: {
      location: location,
      count: props.totalProducts,
    },
    data: undefined,
    ecommerceType: 'impressions',
    ecommerce: {
      currencyCode: currencyCode,
      impressions: [
        {
          ...props.productData,
          list: props.listName,
          position: props.index + 1,
        },
      ],
    },
  }
  trackImpression(payload)
}

const trackClick = () => {
  const payload = {
    group: props.listName,
    ecommerceType: `${props.listName?.replace(' ', '-')?.toLowerCase()}-click`,
    ecommerce: {
      currencyCode: localeConfig[i18n.locale.value]?.currency,
      click: {
        actionField: {
          list: props.listName,
          action: 'click',
        },
        products: [
          {
            ...props.productData,
            list: props.listName,
            position: props.index + 1,
          },
        ],
      },
    },
  }
  pushEventToDataLayer('custom.product.list.click', payload)
  // Push events to Constructor
  if (props.isRecommendation && props.constructorStrategyId) {
    trackRecommendationClick({
      ...constructorItemParam.value,
      podId: props.recommenderID || '',
      strategyId: props.constructorStrategyId,
      resultId: props.constructorResultId,
      resultCount: props.totalProducts,
      resultPositionOnPage: props.index,
    })
  } else if (category === 'Search Page') {
    const term = route.query.q?.toString() || ''
    trackSearchResultClick(term, constructorItemParam.value)
    // Only a click-through from results makes a later conversion attributable to this term.
    attributeSearchClick(term, [constructorItemParam.value.itemId, constructorItemParam.value.variationId])
  } else {
    // PLP
    trackBrowseResultClick({
      ...constructorItemParam.value,
      ...props.constructorEventData,
      filterName: props.constructorEventData?.filterName || 'group_id',
      filterValue: props.constructorEventData?.filterValue || '',
    })
  }
}

const trackStarClick = () => {
  pushEventToDataLayer('custom.product.list.ratings.click', {
    group: 'product',
    category: 'product/list',
    sku: selectedSwatchData.value.id,
    name: selectedSwatchData.value.name,
  })
}

onMounted(() => {
  if (productTile.value) {
    useIntersectionObserver(
      productTile,
      ([entry]) => {
        if (entry && entry.isIntersecting) {
          trackImpressions()
        }
      },
      {
        root: null,
        rootMargin: '0px',
        threshold: 0.5,
      },
    )
  }
})
</script>

<template>
  <div
    ref="productTile"
    v-if="selectedSwatchData.name && selectedSwatchData.defaultImage"
    class="product-tile"
    :class="{ 'product-tile--full-title': fullTitle }"
    data-testid="product-tile"
    :data-cnstrc-item="isRecommendation ? 'recommendation' : undefined"
    :data-cnstrc-item-id="isRecommendation ? constructorItemId : undefined"
    :data-cnstrc-item-name="isRecommendation ? name : undefined"
    :data-cnstrc-item-variation-id="isRecommendation ? id : undefined"
    :data-cnstrc-strategy-id="isRecommendation ? constructorStrategyId : undefined"
    @click="trackClick"
  >
    <div class="product-tile__overlay" v-if="isAlreadyInBag">
      <div class="product-tile__badge">{{ $t('product_tile.already_in_bag') }}</div>
      <Anchor class="product-tile__in-bag-cta" href="/my-bag" variant="primary">
        {{ $t('product_tile.checkout') }}
      </Anchor>
    </div>
    <div class="product-tile__wrapper">
      <a
        class="product-tile__link"
        data-testid="product-tile-link"
        :href="selectedSwatchData.url"
        :target="openInNewTab ? '_blank' : undefined"
      >
        <ProductTileImageSwiper
          v-if="isSwipable"
          :key="selectedSwatchData.id"
          :images="selectedSwatchData.allImages || []"
          :isPriorityImages="!!isPriorityImage"
          :sizes="tileImageSizes"
        />
        <div v-else class="product-tile__images">
          <!-- `loading` before the sources: Vue sets attributes in order, and an image whose srcset lands first
               loads eagerly. -->
          <img
            :loading="isPriorityImage ? 'eager' : 'lazy'"
            :src="selectedSwatchData.defaultImage?.src"
            :alt="selectedSwatchData.defaultImage?.alt"
            :sizes="tileImageSizes"
            :srcset="selectedSwatchData.defaultImage?.srcset"
            :fetchPriority="isPriorityImage ? 'high' : 'auto'"
            class="product-tile__default-image"
            data-testid="product-tile__default-image"
          />
          <img
            v-if="selectedSwatchData.lifestyleImage"
            loading="lazy"
            :src="selectedSwatchData.lifestyleImage?.src"
            :alt="selectedSwatchData.lifestyleImage?.alt"
            :sizes="tileImageSizes"
            :srcset="selectedSwatchData.lifestyleImage?.srcset"
            fetchPriority="low"
            class="product-tile__lifestyle-image"
            data-testid="product-tile__lifestyle-image"
          />
        </div>
        <div class="product-tile__top-items">
          <div v-if="badgeContent.length" class="product-tile__badge-container">
            <div v-for="badge in badgeContent" class="product-tile__badge">{{ badge }}</div>
          </div>
          <WishlistAction
            :id="selectedSwatchData.id"
            :constructorItemParam
            :data-cnstrc-btn="isRecommendation ? 'add_to_wishlist' : undefined"
            class="product-tile__wishlist"
          />
        </div>
      </a>
      <div class="product-tile__bottom-wrapper">
        <Pricing :tiered-prices="prices" />
        <a
          class="product-tile__text-link"
          data-testid="product-tile-name"
          :href="selectedSwatchData.url"
          :target="openInNewTab ? '_blank' : undefined"
        >
          {{ selectedSwatchData.name }}
        </a>
        <a
          v-if="!isRecommendation && rating > 0 && ratingCount > 0"
          class="product-tile__ratings"
          :href="`${selectedSwatchData.url}#pdp-reviews-section`"
          :aria-label="$t('product_tile.ratings.link_label', { name: selectedSwatchData.name })"
          @click="trackStarClick"
        >
          <img
            class="product-tile__ratings-image"
            :alt="$t('product_details_page.review_alt')"
            :src="`/images/stars/${starsFile}`"
          />
          <span class="product-tile__ratings-count">({{ ratingCount }})</span>
        </a>
        <div v-if="filteredPromotions?.length" class="product-tile__promotions">
          <Markdown v-for="promotion in filteredPromotions" :key="promotion.id" :content="promotion.calloutMsg" />
        </div>

        <!-- only show swatches on plp/search -->
        <div v-if="swatches?.length && !isRecommendation" class="product-tile__swatches">
          <button
            v-for="swatch in swatches.slice(0, 4)"
            @click.stop="activeSwatch = swatch"
            class="product-tile__swatch"
            :class="{ 'product-tile__swatch--active': selectedSwatchData.id === swatch.id }"
            :aria-pressed="selectedSwatchData.id === swatch.id"
            data-testid="product-tile-swatch"
            :aria-roledescription="$t('product_tile.text.variation', { variationId: getSwatchSpriteName(swatch) })"
          >
            <svg-icon :name="`swatches:${getSwatchSpriteName(swatch)}`" class="product-tile__swatch-icon" />
          </button>
          <a v-if="unshownSwatchCount > 0" :href="selectedSwatchData.url" class="product-tile__swatch-more"
            >{{ unshownSwatchCount }}+</a
          >
        </div>

        <Action
          v-if="action"
          :action="action"
          variant="text"
          :product-id="selectedSwatchData.id"
          data-testid="product-tile__action"
        >
          <svg-icon class="product-tile__action-icon" name="mhj:bag-add" />
          <div>{{ $t('product_details_page.add_to_bag.button.text') }}</div>
        </Action>
      </div>
    </div>
  </div>
</template>

<style lang="postcss">
.product-tile {
  position: relative;
  height: 100%;
  container-type: inline-size;

  &:has(.product-tile__overlay)::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(1, 1, 1, 0.2);
    z-index: 3;
    pointer-events: none;
  }

  .product-tile__overlay {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 4;
    pointer-events: none;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: var(--space-50);
    padding-inline: var(--space-50);

    @container (max-width: 11rem) {
      height: unset;
      aspect-ratio: var(--aspect-ratio-4-5);
      justify-content: end;
    }
  }

  .product-tile__in-bag-cta {
    min-width: auto;
    white-space: nowrap;
    font-size: var(--text-xsmall);
    padding: var(--space-50) var(--space-60);
    min-height: auto;
    pointer-events: auto;

    @container (max-width: 9.5rem) {
      width: 100%;
      font-size: var(--text-disclaimer);
    }
  }

  .product-tile__wrapper {
    display: flex;
    flex-direction: column;
    align-items: start;
    position: relative;
    margin-bottom: var(--space-50);
  }

  .product-tile__link {
    position: relative;
    width: 100%;
    aspect-ratio: var(--aspect-ratio-4-5);
  }

  .product-tile__images {
    &:has(.product-tile__lifestyle-image) {
      .product-tile__lifestyle-image {
        opacity: 0;
        transition: opacity 0.2s ease-out;

        &:hover {
          transition: opacity 0.2s ease-out;
          opacity: 1;
        }
      }
    }
  }

  .product-tile__default-image,
  .product-tile__lifestyle-image {
    position: absolute;
    top: 0;
    left: 0;
    object-fit: cover;
    aspect-ratio: var(--aspect-ratio-product);
  }

  .product-tile__top-items {
    position: absolute;
    top: 0;
    left: 0;
    padding: var(--space-30);
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    gap: var(--space-30);
    z-index: 2;
  }

  .product-tile__badge-container {
    display: flex;
    flex-direction: column;
    gap: var(--space-20);
    overflow: hidden;
  }

  .product-tile__badge {
    pointer-events: none;
    background-color: var(--surface-light-00);
    color: var(--text-primary);
    padding: var(--space-10) var(--space-30);
    height: fit-content;
    text-transform: uppercase;
    font-size: var(--text-xxsmall);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    width: fit-content;
    max-width: 100%;

    @media (--desktop) {
      font-size: var(--text-xsmall);
    }
  }

  .product-tile__wishlist {
    margin-left: auto;
  }

  .product-tile__bottom-wrapper {
    display: flex;
    flex-direction: column;
    align-items: start;
    position: relative;
    gap: var(--space-30);
    padding: var(--space-30);
  }

  .pricing {
    font-size: var(--text-base);
  }

  .product-tile__text-link {
    overflow: hidden;
    display: -webkit-box;
    line-clamp: 2;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    text-overflow: ellipsis;
    text-decoration: none;
  }

  &.product-tile--full-title {
    @media (--mobile-only) {
      .product-tile__text-link {
        display: block;
        overflow: visible;
        line-clamp: none;
        -webkit-line-clamp: none;
        text-overflow: clip;
      }
    }
  }

  .product-tile__ratings {
    display: flex;
    align-items: center;
    text-decoration: none;
  }

  .product-tile__ratings-image {
    display: block;
  }

  .product-tile__ratings-count {
    font-size: var(--text-small);
    line-height: unset;
    text-align: left;
  }

  .product-tile__promotions {
    display: flex;
    flex-direction: column;

    gap: var(--space-20);
    padding-top: var(--space-30);

    p {
      margin-bottom: 0;
      color: var(--text-sale);
      font-size: var(--text-xxsmall);
      letter-spacing: var(--letter-spacing-default);
      line-height: var(--line-height-default);
      text-transform: uppercase;

      @media (--desktop) {
        font-size: var(--text-xsmall);
      }
    }
  }

  .action {
    text-decoration: none;
    padding-top: var(--space-30);
    display: flex;
    align-items: center;
    gap: var(--space-20);
    text-transform: none;

    div {
      font-size: var(--text-small);

      &:hover {
        text-decoration: underline;
      }
    }
  }

  .product-tile__action-icon {
    width: var(--space-50);
    height: var(--space-50);
    margin-bottom: var(--space-20);
  }

  .product-tile__swatches {
    display: flex;
    gap: var(--space-30);
    align-items: center;
  }

  .product-tile__swatch {
    width: 1.25rem;
    height: 1.25rem;
    border-radius: 100%;
    position: relative;

    &.product-tile__swatch--active {
      &::after {
        content: '';
        display: block;
        width: 1.25rem;
        height: 1.25rem;
        border-radius: 100%;
        outline: 1px solid var(--text-primary);
        outline-offset: 2px;
        position: absolute;
        top: 0;
      }
    }

    &:focus-visible {
      outline: 1px solid var(--status-info);
      outline-offset: 4px;
    }
  }

  .product-tile__swatch-icon {
    width: 1.25rem;
    height: 1.25rem;
  }

  .product-tile__swatch-more {
    font-size: var(--text-xsmall);
  }
}
</style>
